import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Star, Calendar, Phone, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { formatDate, formatTime } from '../utils/helpers';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const ReviewsPage = ({ user, onLogout }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [remarks, setRemarks] = useState('');
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterService, setFilterService] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [services, setServices] = useState([]);

  useEffect(() => {
    fetchReviews();
    fetchServices();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await api.get('/reviews');
      setReviews(response.data);
    } catch (error) {
      toast.error('Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await api.get('/services');
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services');
    }
  };

  const openReviewModal = (review) => {
    setSelectedReview(review);
    setRemarks(review.remarks || '');
  };

  const handleCloseReview = async () => {
    if (!remarks.trim()) {
      toast.error('Please add remarks before closing');
      return;
    }

    try {
      await api.put(`/reviews/${selectedReview.id}`, {
        remarks,
        status: 'closed'
      });
      toast.success('Review closed successfully');
      setSelectedReview(null);
      setRemarks('');
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close review');
    }
  };

  // Filter logic
  const filteredReviews = reviews.filter(review => {
    const matchesSearch = 
      review.patient_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      review.patient_phone.includes(searchQuery);
    
    const matchesService = filterService === 'all' || review.service_name === filterService;
    const matchesDate = !filterDate || review.scheduled_date === filterDate;

    return matchesSearch && matchesService && matchesDate;
  });

  const openReviews = filteredReviews.filter(r => r.status === 'open');
  const closedReviews = filteredReviews.filter(r => r.status === 'closed');

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Review Management</h1>
          <p className="text-sm text-gray-500">Track and manage post-service patient feedback</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <Input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className=""
            />

            <Select value={filterService} onValueChange={setFilterService}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by service" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                {services.map(service => (
                  <SelectItem key={service.id} value={service.name}>
                    {service.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              onClick={() => {
                setSearchQuery('');
                setFilterDate('');
                setFilterService('all');
              }}
              variant="outline"
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : (
          <div className="space-y-6">
            {/* Open Reviews */}
            <div>
              <div className="flex items-center mb-4">
                <div className="h-1 w-12 bg-gradient-to-r from-amber-400 to-amber-600 rounded-full mr-3"></div>
                <h2 className="text-xl font-bold text-gray-900">Open Reviews</h2>
                <span className="ml-3 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                  {openReviews.length}
                </span>
              </div>

              {openReviews.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-200">
                  <p className="text-gray-500">No open reviews</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {openReviews.map(review => (
                    <div
                      key={review.id}
                      className="bg-white border-2 border-amber-200 rounded-xl p-5"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold text-gray-900">{review.patient_name}</h3>
                            <Badge className="bg-purple-100 text-purple-700">
                              {review.service_name}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              <span>{review.patient_phone}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(review.scheduled_date)}</span>
                              {review.scheduled_time && <span className="ml-1 font-medium">at {review.scheduled_time}</span>}
                            </div>
                          </div>
                        </div>
                        <Button
                          onClick={() => openReviewModal(review)}
                          size="sm"
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                        >
                          Add Review & Close
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Closed Reviews */}
            <div>
              <div className="flex items-center mb-4">
                <div className="h-1 w-12 bg-gradient-to-r from-green-400 to-green-600 rounded-full mr-3"></div>
                <h2 className="text-xl font-bold text-gray-900">Closed Reviews</h2>
                <span className="ml-3 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                  {closedReviews.length}
                </span>
              </div>

              {closedReviews.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-200">
                  <p className="text-gray-500">No closed reviews</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {closedReviews.map(review => (
                    <div
                      key={review.id}
                      className="bg-white border border-gray-200 rounded-xl p-5"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold text-gray-900">{review.patient_name}</h3>
                            <Badge className="bg-purple-100 text-purple-700">
                              {review.service_name}
                            </Badge>
                            <Badge className="bg-green-100 text-green-700">Closed</Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                            <div className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              <span>{review.patient_phone}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(review.scheduled_date)}</span>
                              {review.scheduled_time && <span className="ml-1 font-medium">at {review.scheduled_time}</span>}
                            </div>
                          </div>
                          {review.remarks && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                              <p className="text-sm text-gray-700"><strong>Remarks:</strong> {review.remarks}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Review Modal */}
        {selectedReview && (
          <Dialog open={!!selectedReview} onOpenChange={() => setSelectedReview(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-600" />
                  Close Review
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="font-semibold text-gray-900">{selectedReview.patient_name}</p>
                  <p className="text-sm text-gray-600">{selectedReview.service_name}</p>
                  <p className="text-sm text-gray-600">{formatDate(selectedReview.scheduled_date)}</p>
                </div>

                <div>
                  <Label>Remarks / Feedback *</Label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Enter review remarks, feedback, or follow-up notes..."
                    rows={4}
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setSelectedReview(null)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCloseReview}
                  className="flex-1 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white"
                >
                  Close Review
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
};

export default ReviewsPage;
